/** Types for the Sentinel Experiment Lab API contract. */

export type AggOp = 'mean' | 'median' | 'min' | 'max' | 'last_value'
export type GapFill = 'off' | 'locf'
export type BandEstimator = 'iqr' | 'std' | 'abs_quantile'
export type SplitUnit = 'month' | 'year' | 'days' | 'tail_frac' | 'custom'
export type SplitRole = 'train' | 'val' | 'test' | ''
export type SamplingMode = 'aggregate' | 'raw'
export type NormMethod = 'linear' | 'ratio' | 'quadratic' | 'log' | 'power'
export type IntervalUnit = 'seconds' | 'minutes' | 'hours'
/**
 * Where gate cleaning ops drop rows: "all" (default) drops failing rows
 * across the whole window — train, val, AND test; "train_only" drops only
 * gate-failing train/val rows, keeping the test range intact so held-out
 * metrics see every operating regime.
 */
export type GateScope = 'all' | 'train_only'

export const EXPERIMENT_METHOD_KEYS = [
  'target_mlp_none',
  'target_mlp_driver',
  'target_mlp_driver_own',
  'target_knn',
] as const

export type ExperimentMethodKey = (typeof EXPERIMENT_METHOD_KEYS)[number]

export interface CleaningOp {
  op: string
  min?: number
  max?: number
  low?: number
  high?: number
  max_repeat?: number
  window?: number
}

export interface TagCleaning {
  verdict?: string
  notes?: string
  ops: CleaningOp[]
}

export interface CleaningPlan {
  tags: Record<string, TagCleaning>
  summary?: string
}

export interface FeatureKnobs {
  driver_lags: number[]
  rolling_window: number
  driver_ffill_limit: number
  include_self_history: boolean
  include_driver_history: boolean
  phase_shifts?: Record<string, number>
}

export interface SplitSpec {
  unit: SplitUnit
  days?: number
  train_frac?: number
  assignments?: Record<string, string>
  /**
   * Per-chunk tail split (month/year/days units): the last N% (fraction in
   * (0, 1)) of rows within EACH un-assigned chunk become test, the rest
   * train. Explicit `assignments` win per chunk — an assigned chunk keeps
   * its whole-chunk role and is never tail-split.
   */
  chunk_tail_frac?: number
  custom_ranges?: Array<{
    id?: string
    label?: string
    start: string
    end: string
    role?: string
  }>
}

export interface ExperimentConfig {
  target: string
  drivers: string[]
  start_date: string
  end_date: string
  /** aggregate = shared bins; raw = exact historian timestamps outer-joined. */
  sampling_mode: SamplingMode
  interval_minutes: number
  interval: number
  interval_unit: IntervalUnit
  aggregation_op: AggOp
  gap_fill: GapFill
  cleaning_plan: CleaningPlan
  gate_scope: GateScope
  phase_shifts: Record<string, number>
  /** Max |shift| (periods) for cross-correlation scan in data-preview. */
  corr_shift_max: number
  /** Step between shifts in the correlation scan (1 = every period). */
  corr_shift_step: number
  feature: FeatureKnobs
  split: SplitSpec
  /**
   * Per-tag chart Y bounds, keyed by tag id. Applied to the viewer through
   * the pens' customAxisRanges (custom wins over auto) and persisted with
   * the experiment — the backend stores the config dict as-is, so this
   * round-trips through save/load with no backend change.
   */
  y_ranges?: Record<string, { min?: number; max?: number }>
  residual_band_k: number
  band_estimator: BandEstimator
  band_quantile: number
  knn_k: number
  /** Maximum kNN library rows; 0 keeps every eligible training row. */
  knn_fit_cap: number
  fit_cap: number
  /** MLPs score only rows with target + all driver features present (no mask fills). */
  score_complete_rows_only: boolean
  min_usable_rows: number
  min_train_rows: number
  mlp_hidden_layer_sizes: number[]
  mlp_max_iter: number
  random_state: number
  methods: ExperimentMethodKey[]
  notes?: string
}

export interface AlignmentStats {
  total_rows: number
  target_observed?: number
  target_and_all_drivers_exact?: number
  target_and_all_drivers_after_ffill?: number
  target_and_any_driver_after_ffill?: number
  target_and_enough_drivers_for_knn?: number
  knn_min_drivers_required?: number
  driver_ffill_limit?: number
  pairwise_exact_overlap_with_target?: Record<string, number>
  explanation?: string
}

export interface FunnelCounts {
  sampling_mode?: SamplingMode | string
  interval_minutes?: number
  interval?: number
  interval_unit?: string
  aggregation_op?: string | null
  gap_fill?: string
  raw_grid_rows?: number
  raw_observed_per_tag?: Record<string, number>
  after_cleaning_rows?: number
  after_gate_rows?: number
  aligned_grid_rows?: number
  target_observed_rows?: number
  after_warmup_rows?: number
  split_rows?: { train?: number; val?: number; test?: number }
  estimated_fit_rows?: number
  estimated_calib_rows?: number
  train_rows?: number
  val_rows?: number
  score_rows?: number
  alignment?: AlignmentStats
}

export interface TagStats {
  rows: number
  observed: number
  missing_pct: number
  /** Observed points kept after value/gate cleaning (when provided by preview). */
  cleaned_observed?: number
  native_spacing_seconds?: number | null
  longest_gap_periods?: number | null
  min?: number | null
  max?: number | null
  std?: number | null
}

/** Points removed by cleaning (value ops / gate), for chart markers. */
export interface RemovedPointsSeries {
  ts: string[]
  value: (number | null)[]
  /** Parallel to ts/value: "value_op" | "gate" (backend list; may be absent). */
  reason?: string[]
  removed_count?: number
  removed_pct?: number
}

export interface SplitChunk {
  id: string
  label: string
  start: string | null
  end: string | null
  rows: number
  /** train | val | test — or "mixed" when the chunk was tail-split. */
  role: string
  /** Present when role="mixed": rows on each side of the tail split. */
  train_rows?: number
  test_rows?: number
  tail_frac?: number
}

export interface CrossCorrelation {
  shifts: number[]
  by_driver: Record<
    string,
    {
      correlation: (number | null)[]
      best_shift: number
      best_correlation: number | null
    }
  >
}

export interface DataPreviewResponse {
  ok: boolean
  error?: string
  target: string
  drivers: string[]
  window: {
    start: string
    end: string
    interval_minutes?: number | null
    interval?: number
    interval_unit?: string
    sampling_mode?: SamplingMode | string
    aggregation_op: string
    gap_fill: string
  }
  funnel: FunnelCounts
  alignment?: AlignmentStats
  tag_stats: Record<string, TagStats>
  tag_stats_raw?: Record<string, TagStats>
  cleaning_report?: Record<string, unknown>
  split_chunks: SplitChunk[]
  cross_correlation: CrossCorrelation
  /** Per-tag points removed by cleaning; used for red X markers on charts. */
  removed_points?: Record<string, RemovedPointsSeries>
  series: { ts: string[]; [tag: string]: string[] | (number | null)[] }
}

export interface RoleMetrics {
  rows: number
  mae: number | null
  rmse: number | null
  r2: number | null
  residual_std: number | null
  drift: number | null
  score_mae_scaled: number | null
  band_coverage: number | null
}

export interface BandMeta {
  estimator: string
  band_k: number
  band_quantile?: number
  calib_source: string
  calib_rows: number
  fit_rows: number
  err_scale: number
  residual_band: number
  knn_k?: number
}

export interface LossCurve {
  train_loss?: number[]
  val_loss?: number[]
  validation_scores?: number[]
  validation_loss_proxy?: number[]
  n_iter?: number
  best_loss?: number | null
  best_val_loss?: number | null
  streamed?: boolean
}

export interface KnnNeighbor {
  library_index: number
  distance: number | null
  neighbor_y: number | null
  n_shared: number
  xy: [number, number]
}

export interface KnnExample {
  ts: string
  actual_y: number | null
  xy: [number, number]
  neighbors: KnnNeighbor[]
}

export interface KnnInspection {
  k: number
  drivers: string[]
  xy_axes: string[]
  library_size: number
  library_scatter: Array<{ x: number; y: number; target: number }>
  examples: KnnExample[]
}

export interface MethodResult {
  label: string
  description: string
  band_k: number
  band_meta?: BandMeta
  metrics_by_role?: Record<string, RoleMetrics>
  per_tag?: Record<
    string,
    {
      train_rows: number
      score_rows: number
      drift: number | null
      score_std: number | null
      err_scale: number | null
      residual_band: number | null
      score_mae_scaled: number | null
    }
  >
  residual_daily?: {
    date: string[]
    [tag: string]: string[] | (number | null)[]
  }
  expected_daily?: {
    date: string[]
    [tag: string]: string[] | (number | null)[]
  }
  series?: {
    ts: string[]
    actual: (number | null)[]
    expected: (number | null)[]
    residual: (number | null)[]
    role: string[]
  }
  residual_histogram?: { bins: number[]; counts: number[] }
  loss_curve?: LossCurve | null
  knn_inspection?: KnnInspection | null
}

export interface TrainProgressEvent {
  event?: string
  type?: string
  stage?: string
  method?: string
  message?: string
  window?: number
  windows?: number
  rows?: number
  epoch?: number
  max_iter?: number
  train_loss?: number
  val_loss?: number
  early_stop?: boolean
  loss_curve?: LossCurve
  error?: string
  data?: TrainResponse
}

export interface TrainResponse {
  ok: boolean
  error?: string
  target: string
  drivers: string[]
  window: {
    start: string
    end: string
    interval_minutes?: number
    interval?: number
    interval_unit?: string
    sampling_mode?: string
  }
  funnel: FunnelCounts
  alignment?: AlignmentStats
  tag_stats: Record<string, TagStats>
  cleaning_report?: Record<string, unknown>
  training: {
    rows: number
    train_rows: number
    val_rows?: number
    score_rows: number
    split_policy: string
    split_date: string
    knn_skipped?: string | null
    validation_note?: string
  }
  methods: Record<string, MethodResult>
  split_chunks: SplitChunk[]
  resolved_config: Record<string, unknown>
  warnings?: string[]
  /**
   * Entry the server filed this run under when the job finished, so the run
   * survives the tab closing mid-train. The lab adopts it as the save target
   * so a later manual save renames that entry instead of duplicating it.
   */
  autosaved_entry_id?: string
  /** Set instead when the autosave failed; the run itself is still valid. */
  autosave_error?: string
}

export interface NormalizeSeries {
  ts: string[]
  actual: (number | null)[]
  normalized: (number | null)[]
}

/**
 * Fit coefficients, shaped per method (backend `_normalize_fit`):
 * linear → intercept + slopes; quadratic → intercept + slopes + quadratic
 * (x² terms); log → intercept + slopes (on ln x) + n_valid; power →
 * scale + exponents + n_valid (y = scale · Π xᵢ^exponentᵢ); ratio → null.
 */
export interface NormalizeCoefficients {
  intercept?: number
  slopes?: Record<string, number>
  quadratic?: Record<string, number>
  scale?: number
  exponents?: Record<string, number>
  /** log/power: rows that survived the positivity guard. */
  n_valid?: number
}

/** One per-driver normalize variant (single against tag). */
export interface NormalizeVariant {
  method: NormMethod
  against: string
  flatness?: number
  variance_reduction_pct?: number
  std_before?: number
  std_after?: number
  n_points?: number
  coefficients?: NormalizeCoefficients | null
  /** Shape-free rank correlation vs target (present for every method). */
  spearman?: number | null
  error?: string
  series: NormalizeSeries
}

export interface NormalizeResponse {
  ok: boolean
  error?: string
  method: NormMethod
  target: string
  /** Combined against tags (all selected). */
  against: string[]
  /** Combined metrics: linear = all tags; ratio = vs first tag. */
  flatness: number
  variance_reduction_pct: number
  std_before: number
  std_after: number
  n_points: number
  coefficients?: NormalizeCoefficients | null
  /** Spearman rank correlation vs target per against tag (all methods). */
  spearman?: Record<string, number | null>
  series: NormalizeSeries
  /** Per-driver fan-out; shares series.ts / actual with top-level. */
  by_against?: NormalizeVariant[]
}

export interface ExperimentRun {
  id: string
  createdAt: string
  label: string
  config: ExperimentConfig
  result: TrainResponse
}

export interface SavedExperimentSummary {
  entry_id: string
  name: string
  updated_at?: string
  target?: string
  drivers?: number
  has_result?: boolean
  has_models?: boolean
  methods?: string[]
  split_date?: string
  notes?: string
  /** Filed by the server when the run finished, not by a user pressing save. */
  autosaved?: boolean
  /** Rolodex entry.created_by (Auth0 id or email). */
  created_by?: string | null
  creator_name?: string | null
  creator_email?: string | null
  /** Preferred display identity: name, else email, else created_by. */
  creator?: string | null
}

/** Dropdown option text: `name · short(target) · creator`. */
export const formatSavedExperimentLabel = (
  item: SavedExperimentSummary
): string => {
  const shortTarget = item.target
    ? item.target.split('-').slice(1).join('-') || item.target
    : ''
  const who = (
    item.creator ||
    item.creator_name ||
    item.creator_email ||
    ''
  ).trim()
  return [item.name, shortTarget || null, who || null]
    .filter(Boolean)
    .join(' · ')
}

export interface SaveExperimentResponse {
  ok: boolean
  entry_id: string
  name: string
  has_result: boolean
  has_models: boolean
  warnings?: string[]
  error?: string
}

export interface LoadExperimentResponse {
  ok: boolean
  entry_id: string
  name?: string
  notes?: string
  summary?: Record<string, unknown>
  config: Partial<ExperimentConfig> & Record<string, unknown>
  result?: TrainResponse | null
  /** Newest-first comparison history from the saved experiment's runs.json. */
  runs?: ExperimentRun[]
  warnings?: string[]
  has_models?: boolean
  files?: Record<string, string>
  error?: string
}

export const defaultExperimentConfig = (): ExperimentConfig => {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - 730)
  return {
    target: '',
    drivers: [],
    start_date: start.toISOString().slice(0, 10),
    end_date: end.toISOString().slice(0, 10),
    sampling_mode: 'aggregate',
    interval_minutes: 10,
    interval: 10,
    interval_unit: 'minutes',
    aggregation_op: 'mean',
    gap_fill: 'off',
    cleaning_plan: { tags: {}, summary: '' },
    gate_scope: 'all',
    phase_shifts: {},
    corr_shift_max: 36,
    corr_shift_step: 1,
    feature: {
      driver_lags: [1, 3, 6],
      rolling_window: 6,
      driver_ffill_limit: 2,
      include_self_history: true,
      include_driver_history: true,
    },
    split: {
      unit: 'tail_frac',
      train_frac: 0.9,
      assignments: {},
    },
    residual_band_k: 2,
    band_estimator: 'iqr',
    band_quantile: 0.9,
    knn_k: 25,
    knn_fit_cap: 0,
    fit_cap: 30000,
    score_complete_rows_only: false,
    min_usable_rows: 200,
    min_train_rows: 100,
    mlp_hidden_layer_sizes: [64, 32],
    mlp_max_iter: 300,
    random_state: 17,
    methods: [...EXPERIMENT_METHOD_KEYS],
  }
}

/** Merge a loaded Rolodex config onto lab defaults so missing knobs stay sane. */
export const mergeLoadedConfig = (
  loaded: Partial<ExperimentConfig> & Record<string, unknown>
): ExperimentConfig => {
  const base = defaultExperimentConfig()
  const feature = {
    ...base.feature,
    ...((loaded.feature as FeatureKnobs | undefined) || {}),
  }
  const split = {
    ...base.split,
    ...((loaded.split as SplitSpec | undefined) || {}),
  }
  const methods = Array.isArray(loaded.methods)
    ? (loaded.methods as ExperimentMethodKey[]).filter((m) =>
        (EXPERIMENT_METHOD_KEYS as readonly string[]).includes(m)
      )
    : base.methods
  return {
    ...base,
    ...loaded,
    feature,
    split,
    cleaning_plan: (loaded.cleaning_plan as CleaningPlan) || base.cleaning_plan,
    gate_scope: loaded.gate_scope === 'train_only' ? 'train_only' : 'all',
    phase_shifts: (loaded.phase_shifts as Record<string, number>) || {},
    corr_shift_max:
      typeof loaded.corr_shift_max === 'number'
        ? loaded.corr_shift_max
        : base.corr_shift_max,
    corr_shift_step:
      typeof loaded.corr_shift_step === 'number'
        ? loaded.corr_shift_step
        : base.corr_shift_step,
    methods: methods.length ? methods : base.methods,
    sampling_mode: (loaded.sampling_mode as SamplingMode) || base.sampling_mode,
    interval_unit: (loaded.interval_unit as IntervalUnit) || base.interval_unit,
    interval:
      typeof loaded.interval === 'number'
        ? loaded.interval
        : typeof loaded.interval_minutes === 'number'
          ? loaded.interval_minutes
          : base.interval,
    interval_minutes:
      typeof loaded.interval_minutes === 'number'
        ? loaded.interval_minutes
        : base.interval_minutes,
    target: typeof loaded.target === 'string' ? loaded.target : base.target,
    drivers: Array.isArray(loaded.drivers)
      ? (loaded.drivers as string[])
      : base.drivers,
  }
}
